
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { SubscriptionPlan } from '@/services/subscriptions';

interface SubscriptionPlansProps {
  plans: SubscriptionPlan[];
  currentSubscriptionTier?: string;
  isUpgrading: boolean;
  upgradeTier: string | null;
  handleUpgrade: (tier: string) => void;
}

const SubscriptionPlans = ({
  plans,
  currentSubscriptionTier,
  isUpgrading,
  upgradeTier,
  handleUpgrade
}: SubscriptionPlansProps) => {
  // Filter out unlimited plan from display
  const visiblePlans = plans.filter((plan) => plan.tier !== 'unlimited');

  return (
    <>
      {visiblePlans.map((plan) =>
      <Card
        key={plan.id}
        className={`
            relative flex flex-col bg-white/[0.03] backdrop-blur-sm
            ${plan.recommended ?
        'border-2 border-primary bg-white/[0.05] scale-[1.02] z-10' :
        'border border-border hover:border-border/80'}
            transition-all duration-200 rounded-xl overflow-hidden
          `
        }>

          {plan.recommended &&
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
              <div className="bg-primary text-white px-4 text-xs font-semibold shadow-md rounded-full my-[10px] py-[5px]">
                RECOMMENDED
              </div>
            </div>
        }
          
          <CardHeader className="text-center pt-8 pb-4 px-6">
            <CardTitle className="text-2xl font-bold mb-2">{plan.name}</CardTitle>
            <CardDescription className="text-sm text-muted-foreground mb-4">
              {plan.description}
            </CardDescription>
            
            <div className="mb-4">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold tracking-tight">
                  {plan.priceDisplay.split('/')[0]}
                </span>
                {plan.price > 0 &&
              <span className="text-base text-muted-foreground">/month</span>
              }
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="flex-grow px-6 pb-6">
            <ul className="space-y-3">
              {plan.features.map((feature, index) =>
            <li key={index} className="flex items-start gap-3">
                  <div className="shrink-0 mt-0.5">
                    <Check className="h-5 w-5 text-green-400" />
                  </div>
                  <span className="text-sm leading-relaxed">{feature}</span>
                </li>
            )}
            </ul>
          </CardContent>
          
          <CardFooter className="px-6 pb-6 pt-0">
            <Button
            onClick={() => handleUpgrade(plan.tier)}
            className={`
                w-full h-11 font-medium
                ${plan.recommended ? 'shadow-md' : 'shadow-sm'}
              `}
            variant={plan.tier === currentSubscriptionTier ? "outline" : "default"}
            disabled={isUpgrading || plan.tier === currentSubscriptionTier}>

            {(() => {
              const tierOrder: Record<string, number> = { free: 0, starter: 1, pro: 2, unlimited: 3 };
              const currentRank = currentSubscriptionTier ? tierOrder[currentSubscriptionTier] ?? -1 : -1;
              const planRank = tierOrder[plan.tier] ?? 0;

              if (isUpgrading && upgradeTier === plan.tier) {
                return <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing...</>;
              }
              if (plan.tier === currentSubscriptionTier) return 'Current Plan';
              if (planRank < currentRank) return 'Downgrade';
              return plan.price === 0 ? 'Select Free Plan' : 'Upgrade';
            })()}
            </Button>
          </CardFooter>
        </Card>
      )}
    </>);

};

export default SubscriptionPlans;